import { action, computed, observable, ObservableMap } from "mobx";
import { QuizObject } from "../quiz_objects/quiz-object";
import { axios } from "../../../utils/axios";

function pushUnique(array: any, value: any) {
    if (array.includes(value)) return;
    array.push(value);
}

export default class Subject {
    @observable slug: string = "";
    @observable _id: string = "";
    @observable title: string = "";
    @observable editable: boolean = false;
    @observable description: string = "";
    @observable user: string = "";
    @observable _image_url: string = "";
    @observable pexels_url: string = "";
    @observable objects: Map<string, QuizObject> = new Map();
    @observable sortedQuizObjects: Array<string> = [];
    @observable quizObjectCount: number = 0;
    @observable public: boolean = true;
    @observable enabled: boolean = true;
    @observable user_count: number = 0;
    @observable users: Array<string> = [];

    constructor(
        slug: string,
        subject: any,
        objects?: any,
        permissions?: any,
        tracking?: any,
    ) {
        if (subject) {
            this._id = subject._id;
            this.description = subject.description;
            this.slug = subject.slug;
            this.user = subject.user;
            this.title = subject.title;
            this._image_url = subject.image_url;
            this.pexels_url = subject.pexels_url || "";
            this.public = subject.public ?? false;
            this.enabled = subject.enabled ?? true;
            if (subject.users) {
                this.user_count = subject.users.length;
            }
            if (objects) {
                this.objects = this.apply_new_objects(objects, tracking);
            }
            this.quizObjectCount = this.applyObjectCount(subject);
            if (subject.sorted_quiz_objects) {
                this.setQuizObjectOrder(subject.sorted_quiz_objects.list);
            }
        }

        if (permissions) {
            this.editable = permissions.can_edit;
        }
    }

    @computed
    get canEdit(): boolean {
        return this.editable;
    }

    @computed
    get hash_key() {
        return `${this.user}/${this.slug}`;
    }

    @computed
    get allFacts() {
        const allFacts: Array<any> = [];

        this.objects.forEach(object => {
            object.facts.forEach(fact => {
                pushUnique(allFacts, fact.name);
            });
        });

        return allFacts;
    }

    @action
    save(cb: Function) {
        var store = this;
        axios()
            .put(`/subjects/${this.user}/${this.slug}`, {
                title: this.title,
                description: this.description,
                public: this.public,
                pexels_url: this.pexels_url
            })
            .then(function(res: any) {
                store.title = res.data.subject.title;
                store.slug = res.data.subject.slug;
                store.description = res.data.subject.description;
                store.public = res.data.subject.public;
                cb(undefined, res.data.subject.slug);
            })
            .catch(function(err: any) {
                cb(err?.response?.data || { title: "Could not save subject" }, undefined);
            });
    }

    setPexelsURL(pexels_url: string) {
        this.pexels_url = pexels_url;
    }
    get_num_subjects(subjects: any): number {
        if (subjects instanceof ObservableMap) {
            return subjects.size;
        }
        return Object.keys(subjects).length;
    }

    apply_new(old_value: string, new_value: string) {
        if (new_value) {
            return new_value;
        }
        return old_value;
    }

    get_num_objects(objects: any): number {
        if (objects instanceof ObservableMap) {
            return objects.size;
        }
        return Object.keys(objects).length;
    }

    getTracking(tracking: Array<any>, object_id: any) {
        let retVal: any = {
            dataType: "quiz_object",
            score: 0
        };

        if (!tracking) {
            return [retVal];
        }

        tracking.forEach(oneTracking => {
            if (object_id == oneTracking.quiz_object) {
                if (oneTracking.score) {
                    retVal.score = oneTracking.score;
                }
            }
        });

        return [
            {
                dataType: "quiz_object",
                score: retVal.score
            }
        ];
    }

    object_to_map(objects: any, tracking?: any): Map<string, QuizObject> {
        var retval = new Map();

        Object.keys(objects).forEach(object => {
            retval.set(
                objects[object]._id,
                new QuizObject(
                    objects[object]._id,
                    objects[object],
                    this.getTracking(tracking, objects[object]._id)
                )
            );
        });

        const object_keys: Array<string> = Array.from(retval.keys());

        return retval;
    }

    public setTracking(trackings: Array<any>) {
        trackings.forEach((tracking: any) => {
            const quizObject = this.objects.get(tracking.quiz_object);
            
            if (!quizObject) { return };
            quizObject.applyTracking([
                {
                    dataType: "quiz_object",
                    score: tracking.score
                }
            ]);

            this.objects.set(tracking.quiz_object, quizObject);
        });
    }

    apply_new_objects(objects: any, tracking?: any): Map<string, QuizObject> {
        if (objects) {
            if (this.objects) {
                if (
                    this.get_num_objects(objects) >=
                    this.get_num_objects(this.objects)
                ) {
                    return this.object_to_map(objects, tracking);
                }
            } else {
                return this.object_to_map(objects, tracking);
            }
        }
        return this.objects;
    }

    addObject(object: any, optional_id?: string) {
        if (object.new && optional_id) {
            return this.objects.set(
                optional_id,
                new QuizObject(object.slug, object)
            );
        }
        this.objects.set(object.slug, new QuizObject(object.slug, object));
    }

    setQuizObjectOrder(sorted_quiz_objects: any[]) {
        if (!sorted_quiz_objects) return;
        let ids = new Array(sorted_quiz_objects.length);
        sorted_quiz_objects.forEach(quiz_object => {
            ids[quiz_object.position] = quiz_object.quiz_object;
        });
        this.sortedQuizObjects = ids;
    }

    isLastQuizObject(objectID: string) {
        const index = this.sortedQuizObjects.indexOf(objectID);
        const lastIndex = this.sortedQuizObjects.length - 1;
        return index === lastIndex;
    }

    moveObject(move_object_id: any, after_object_id: any) {
        const subject = this;
        let move_to_end = false;

        if (!after_object_id) move_to_end = true;

        if (move_object_id === after_object_id) {
            return;
        }

        axios()
            .put(`/subjects/${this.user}/${this.slug}/move`, {
                move_object_id: move_object_id,
                after_object_id: after_object_id,
                move_to_end: move_to_end
            })
            .then(function(res: any) {
                subject.setQuizObjectOrder(res.data.sorted_quiz_objects);
            })
            .catch(function(err: any) {
                if (err.response) {
                    return console.log(err.response.data);
                }
                return console.log(err);
            });
    }

    @computed
    get image_url() {
        return this._image_url;
    }

    applyObjectCount(subject: any): number {
        if (subject.quizObjectCount) {
            return subject.quizObjectCount;
        }
        return subject.object_count;
    }

}
